import { useContext, useEffect, useState } from 'react'

import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { CriterionDto, GroupedCriteriaDto } from '../../../../api/generated'
import { colors } from '../../../../constants/colors'
import FilterStorage from '../../../../storage/FilterStorage/FilterStorage'
import { AllFilterButton, FlexColumn, FlexRowBetween } from '../../../projectComponents'
import { isStringChoiceCriterion, StringCriteriaModal } from './StringCriteriaModal'
import { SelectColorComponent } from './SelectColorComponent'
import { FilterInput as TextField, FilterRange as Range, SidebarTitle } from './filterStyles'
import { InputSwitchWithTitle } from '../../InputSwitchWithTitle'

const Block = styled(FlexColumn)`
  width: 100%;
  align-items: stretch;
  min-width: 0;
`

const Group = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const GroupItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const FilterTitle = styled.h3`
  margin: 0;
  color: ${colors.black};
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
`

const Card = styled(Block)`
  padding: 10px;
  border: 4px solid ${colors.lightGray};
  border-radius: 12px;
  box-sizing: border-box;
  background: ${colors.white};
  > h3 {
    padding-bottom: 8px;
    border-bottom: 1px solid ${colors.lightGrey};
  }
`

const Options = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  gap: 6px;
`

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  border-bottom: 1px solid ${colors.lightGrey};
`

const Option = styled.label`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  input {
    appearance: none;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin: 0;
    cursor: pointer;
    background: url('/images/buttons/filter-checkbox-inactive.svg') center / 20px 20px no-repeat;
  }
  input:checked {
    background-image: url('/images/buttons/checkbox-active.svg');
  }
  input:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 3px;
  }
`

const Field = styled.div`
  min-width: 0;
`

const NumericSelect = styled.select`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 14px 16px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: ${colors.lightGray};
  color: ${colors.black};
  font: inherit;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`

const COLOR_NAMES: Record<string, string> = {
  белый: '#FFFFFF',
  бежевый: '#F5F5DC',
  голубой: '#87CEEB',
  желтый: '#FFD700',
  жёлтый: '#FFD700',
  зеленый: '#2E8B57',
  зелёный: '#2E8B57',
  коричневый: '#8B4513',
  красный: '#DC143C',
  оранжевый: '#FF8C00',
  розовый: '#FFC0CB',
  серый: '#808080',
  синий: '#1E5AA8',
  фиолетовый: '#800080',
  черный: '#111111',
  чёрный: '#111111',
}

const isColorCriterion = (criterion: CriterionDto) =>
  /(цвет|color)/i.test(`${criterion.name ?? ''} ${criterion.propertyName ?? ''}`)

const resolveColor = (value: string) => {
  const normalized = value.trim().toLowerCase()
  if (COLOR_NAMES[normalized]) return COLOR_NAMES[normalized]
  if (/^(#([\da-f]{3,8})|rgba?\(.+\)|hsla?\(.+\)|[a-z]+)$/i.test(normalized)) return normalized
  return colors.middleGray
}

const titleFor = (criterion: CriterionDto) =>
  criterion.propertyName || criterion.name || 'Характеристика'

const CriteriaOptions = observer(function CriteriaOptions({
  criterion,
  productValues = false,
}: {
  criterion: CriterionDto
  productValues?: boolean
}) {
  const store = useContext(FilterStorage)
  const criterionId = criterion.id
  if (!criterionId) return null

  const stringValues = criterion.possibleStringValues ?? []
  const numberValues = criterion.possibleNumberValues ?? []
  const selectedStrings =
    store.sorting.stringOneOfFilters?.find(filter => filter.criterionId === criterionId)?.values ??
    []
  const selectedNumbers =
    store.sorting.numberOneOfFilters?.find(filter => filter.criterionId === criterionId)?.values ??
    []
  const selectedExact = store.sorting.stringExactFilters?.find(
    filter => filter.criterionId === criterionId
  )?.value
  const toggleString = (value: string) =>
    store.setStringOneOfFilter(
      criterionId,
      selectedStrings.includes(value)
        ? selectedStrings.filter(item => item !== value)
        : [...selectedStrings, value]
    )
  const toggleNumber = (value: number) =>
    store.setNumberOneOfFilter(
      criterionId,
      selectedNumbers.includes(value)
        ? selectedNumbers.filter(item => item !== value)
        : [...selectedNumbers, value]
    )

  if (
    productValues &&
    (criterion.filterType === 'numberOneOf' || criterion.filterType === 'numberFromTo')
  ) {
    return (
      <Block style={{ gap: 12 }}>
        <FilterTitle>{titleFor(criterion)}</FilterTitle>
        <NumericSelect
          aria-label={titleFor(criterion)}
          disabled={!numberValues.length}
          value={selectedNumbers[0] ?? ''}
          onChange={event =>
            store.setNumberOneOfFilter(
              criterionId,
              event.target.value === '' ? [] : [Number(event.target.value)]
            )
          }
        >
          <option value="">
            {numberValues.length ? 'Выберите значение' : 'Нет допустимых значений'}
          </option>
          {[...new Set(numberValues)].map(value => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </NumericSelect>
      </Block>
    )
  }

  if (
    (criterion.filterType === 'stringOneOf' || criterion.filterType === 'stringExact') &&
    stringValues.length === 1
  ) {
    const [value] = stringValues
    const isEnabled =
      criterion.filterType === 'stringOneOf'
        ? selectedStrings.includes(value)
        : selectedExact === value
    return (
      <InputSwitchWithTitle
        sidebar
        subtitle={value}
        switchOn={isEnabled}
        title={titleFor(criterion)}
        toggleSwitch={() =>
          criterion.filterType === 'stringOneOf'
            ? store.setStringOneOfFilter(criterionId, isEnabled ? [] : [value])
            : store.setStringValueFilter('stringExact', criterionId, isEnabled ? undefined : value)
        }
      />
    )
  }

  if (criterion.filterType === 'stringOneOf' && isColorCriterion(criterion)) {
    const selectedIds = stringValues.flatMap((value, index) =>
      selectedStrings.includes(value) ? [index] : []
    )
    return (
      <SelectColorComponent
        compact
        colorIds={selectedIds}
        colors={stringValues.map(resolveColor)}
        labels={stringValues}
        setColorIds={(ids: number[]) =>
          store.setStringOneOfFilter(
            criterionId,
            ids.map(index => stringValues[index]).filter(Boolean)
          )
        }
        title={titleFor(criterion)}
      />
    )
  }

  if (
    criterion.filterType === 'stringOneOf' ||
    (criterion.filterType === 'stringExact' && stringValues.length > 0) ||
    (criterion.filterType === 'stringTerm' && stringValues.length > 0) ||
    (criterion.filterType === 'numberOneOf' && numberValues.length > 1)
  ) {
    const values = criterion.filterType === 'numberOneOf' ? numberValues : stringValues
    const selectedTerm = store.sorting.stringTermFilters?.find(
      filter => filter.criterionId === criterionId
    )?.value
    return (
      <Card>
        <FilterTitle>{titleFor(criterion)}</FilterTitle>
        <Options>
          {values.map(value => {
            const checked =
              typeof value === 'number'
                ? selectedNumbers.includes(value)
                : criterion.filterType === 'stringExact'
                  ? selectedExact === value
                  : criterion.filterType === 'stringTerm'
                    ? selectedTerm === value
                    : selectedStrings.includes(value)
            return (
              <OptionRow key={String(value)}>
                <Option>
                  <span title={String(value)}>{value}</span>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      if (typeof value === 'number') toggleNumber(value)
                      else if (
                        criterion.filterType === 'stringExact' ||
                        criterion.filterType === 'stringTerm'
                      )
                        store.setStringValueFilter(
                          criterion.filterType,
                          criterionId,
                          checked ? undefined : value
                        )
                      else toggleString(value)
                    }}
                  />
                </Option>
              </OptionRow>
            )
          })}
        </Options>
      </Card>
    )
  }

  return <CriterionValueInput criterion={criterion} />
})

const CriterionValueInput = observer(function CriterionValueInput({
  criterion,
}: {
  criterion: CriterionDto
}) {
  const store = useContext(FilterStorage)
  const criterionId = criterion.id!
  const existingRange = store.sorting.numberFromToFilters?.find(
    filter => filter.criterionId === criterionId
  )
  const existingText = (
    criterion.filterType === 'stringExact'
      ? store.sorting.stringExactFilters
      : store.sorting.stringTermFilters
  )?.find(filter => filter.criterionId === criterionId)?.value
  const existingNumber = store.sorting.numberOneOfFilters?.find(
    filter => filter.criterionId === criterionId
  )?.values?.[0]
  const [value, setValue] = useState(existingText ?? (existingNumber?.toString() || ''))
  const [from, setFrom] = useState(existingRange?.valueFrom?.toString() || '')
  const [to, setTo] = useState(existingRange?.valueTo?.toString() || '')
  useEffect(() => {
    setValue(existingText ?? existingNumber?.toString() ?? '')
  }, [existingText, existingNumber])
  useEffect(() => {
    setFrom(existingRange?.valueFrom?.toString() ?? '')
    setTo(existingRange?.valueTo?.toString() ?? '')
  }, [existingRange?.valueFrom, existingRange?.valueTo])
  const validError = from && to && Number(from) > Number(to) ? 'Минимум больше максимума' : ''

  if (criterion.filterType === 'numberFromTo') {
    const limits = criterion.possibleNumberValues ?? []
    const min = limits.length ? Math.min(...limits) : undefined
    const max = limits.length ? Math.max(...limits) : undefined
    const commit = () => {
      if (validError) return
      store.setNumberFromToFilter(
        criterionId,
        from === '' ? undefined : Number(from),
        to === '' ? undefined : Number(to)
      )
    }
    return (
      <Block>
        <FilterTitle style={{ marginBottom: 12 }}>{titleFor(criterion)}</FilterTitle>
        <Range>
          <Field>
            <TextField
              label="От"
              inputProps={{
                'aria-label': `${titleFor(criterion)}: от`,
                step: 'any',
                min,
                max,
                onChange: event => setFrom(event.target.value),
                placeholder: 'От',
                type: 'number',
                value: from,
              }}
              error={validError}
              onBlur={commit}
              size={'md'}
              fullwidth
            />
          </Field>
          <Field>
            <TextField
              label="До"
              inputProps={{
                'aria-label': `${titleFor(criterion)}: до`,
                step: 'any',
                min,
                max,
                onChange: event => setTo(event.target.value),
                placeholder: 'До',
                type: 'number',
                value: to,
              }}
              onBlur={commit}
              size={'md'}
              fullwidth
            />
          </Field>
        </Range>
      </Block>
    )
  }

  const isNumber = criterion.filterType === 'numberOneOf'
  const commit = () => {
    if (isNumber) store.setNumberOneOfFilter(criterionId, value === '' ? [] : [Number(value)])
    else if (criterion.filterType === 'stringExact' || criterion.filterType === 'stringTerm')
      store.setStringValueFilter(criterion.filterType, criterionId, value.trim() || undefined)
  }
  return (
    <Block>
      <FilterTitle style={{ marginBottom: 12 }}>{titleFor(criterion)}</FilterTitle>
      <TextField
        inputProps={{
          'aria-label': titleFor(criterion),
          step: isNumber ? 'any' : undefined,
          onChange: event => setValue(event.target.value),
          placeholder: isNumber ? 'Введите число' : 'Введите значение',
          type: isNumber ? 'number' : 'text',
          value,
        }}
        onBlur={commit}
        fullwidth
      />
    </Block>
  )
})

export const DynamicCriterionFilters = ({
  groups,
  productValues = false,
}: {
  groups: GroupedCriteriaDto[]
  productValues?: boolean
}) => {
  const store = useContext(FilterStorage)
  const [expanded, setExpanded] = useState<GroupedCriteriaDto | null>(null)
  const clearGroup = (group: GroupedCriteriaDto) => {
    group.criteria?.forEach(criterion => {
      if (!criterion.id) return
      store.setStringOneOfFilter(criterion.id, [])
      store.setNumberOneOfFilter(criterion.id, [])
      store.setStringValueFilter('stringExact', criterion.id)
      store.setStringValueFilter('stringTerm', criterion.id)
      store.setNumberFromToFilter(criterion.id)
    })
  }
  return (
    <>
      {groups
        .filter(group => group.criteria?.length)
        .map((group, index) => (
          <Group
            key={`${group.groupName}-${index}`}
            aria-label={group.groupName || 'Характеристики'}
          >
            <FlexRowBetween>
              <SidebarTitle>{group.groupName || 'Характеристики'}</SidebarTitle>
              {group.groupName?.trim().toLowerCase() !== 'цвета' && (
                <AllFilterButton
                  type="button"
                  onClick={() =>
                    group.criteria.some(isStringChoiceCriterion)
                      ? setExpanded(group)
                      : clearGroup(group)
                  }
                  aria-label={`${group.criteria.some(isStringChoiceCriterion) ? 'Все варианты группы' : 'Сбросить фильтры группы'} ${group.groupName || 'Характеристики'}`}
                >
                  Все
                </AllFilterButton>
              )}
            </FlexRowBetween>
            <GroupItems>
              {group.criteria.map(criterion => (
                <CriteriaOptions
                  criterion={criterion}
                  key={criterion.id}
                  productValues={productValues}
                />
              ))}
            </GroupItems>
          </Group>
        ))}
      {expanded && <StringCriteriaModal group={expanded} onClose={() => setExpanded(null)} />}
    </>
  )
}
